import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CalendarPlus,
  FileCheck2,
  FileMinus2,
  Lock,
  OctagonX,
  RotateCcw,
  SkipForward,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import {
  createGovernedOperationKey,
  useGovernedStageOperation,
} from '@/hooks/useGovernedStageOperation';
import {
  resolveLateStageActions,
  type GovernedStageOperation,
  type LateStageActionId,
  type LateStageSummary,
} from '@/lib/forkhire/workflow/lateStageWorkflow';

type Props = {
  applicationId: string;
  stage: string;
  pipelineAllowed: boolean;
  summary?: LateStageSummary;
  summaryLoading?: boolean;
  dense?: boolean;
  includeDisqualify?: boolean;
  onScheduleF2F: () => void;
  onMoveToF2F: () => void;
  onMoveToContract: () => void;
  onOperationSuccess?: () => void;
};

const iconByAction: Record<LateStageActionId, typeof SkipForward> = {
  skip_ai_interview: SkipForward,
  end_ai_interview_to_f2f: OctagonX,
  move_to_f2f: SkipForward,
  rollback_f2f_to_ai_interview: RotateCcw,
  schedule_f2f: CalendarPlus,
  skip_f2f: SkipForward,
  rollback_offer_to_f2f: Undo2,
  move_to_contract: FileCheck2,
  skip_offer: FileMinus2,
  rollback_contract_to_offer: Undo2,
  skip_contract: FileCheck2,
  disqualify: SkipForward,
};

export function LateStageWorkflowActions({
  applicationId,
  stage,
  pipelineAllowed,
  summary,
  summaryLoading = false,
  dense = false,
  includeDisqualify = false,
  onScheduleF2F,
  onMoveToF2F,
  onMoveToContract,
  onOperationSuccess,
}: Props) {
  const { t } = useTranslation();
  const operation = useGovernedStageOperation();
  const [pending, setPending] = useState<{
    operation: GovernedStageOperation;
    idempotencyKey: string;
    sessionId?: string;
  } | null>(null);
  const [note, setNote] = useState('');
  const [reasonCode, setReasonCode] = useState('');

  const canHaveActiveInterview = ['shortlisted', 'shortlisting', 'interviewed'].includes(stage);
  const { data: inProgressSession, isLoading: inProgressSessionLoading } = useQuery({
    queryKey: ['active-interview-sessions', applicationId, 'in-progress'],
    enabled: canHaveActiveInterview,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('interview_sessions')
        .select('id, status')
        .eq('application_id', applicationId)
        .eq('status', 'in_progress')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 10_000,
  });

  const actions = useMemo(() => {
    const resolved = resolveLateStageActions({
      stage,
      pipelineAllowed,
      summary: summary ?? {
        hasAcceptedOffer: false,
        hasSignedContract: false,
        hasAiInterviewBasis: false,
        allowedOperations: [],
        denialReasons: {},
      },
    });
    return resolved
      .filter((item) => includeDisqualify || item.id !== 'disqualify')
      .map((item) => {
        if (
          inProgressSession?.id &&
          ['skip_ai_interview', 'move_to_f2f', 'schedule_f2f'].includes(item.id)
        ) {
          return {
            id: 'end_ai_interview_to_f2f' as const,
            blockedReason: pipelineAllowed ? null : 'candidate_pipeline_unavailable',
          };
        }
        return {
          ...item,
          blockedReason: item.blockedReason ??
          (inProgressSessionLoading && ['skip_ai_interview', 'move_to_f2f', 'schedule_f2f'].includes(item.id)
            ? 'state_check_pending'
            : null) ??
          (summaryLoading && [
            'skip_ai_interview',
            'move_to_f2f',
            'rollback_f2f_to_ai_interview',
            'rollback_offer_to_f2f',
            'move_to_contract',
            'skip_offer',
            'rollback_contract_to_offer',
            'skip_contract',
          ].includes(item.id)
            ? 'state_check_pending'
            : null),
        };
      });
  }, [
    includeDisqualify,
    pipelineAllowed,
    stage,
    summary,
    summaryLoading,
    inProgressSession?.id,
    inProgressSessionLoading,
  ]);

  const requestAction = (actionId: LateStageActionId) => {
    if (actionId === 'schedule_f2f') {
      onScheduleF2F();
      return;
    }
    if (actionId === 'move_to_f2f') {
      onMoveToF2F();
      return;
    }
    if (actionId === 'move_to_contract') {
      onMoveToContract();
      return;
    }
    setNote('');
    setReasonCode('');
    setPending({
      operation: actionId,
      idempotencyKey: createGovernedOperationKey(actionId, applicationId),
      sessionId: actionId === 'end_ai_interview_to_f2f' ? inProgressSession?.id : undefined,
    });
  };

  const confirm = () => {
    if (!pending) return;
    operation.mutate({
      applicationId,
      sessionId: pending.sessionId,
      operation: pending.operation,
      idempotencyKey: pending.idempotencyKey,
      reasonCode: pending.operation === 'end_ai_interview_to_f2f'
        ? reasonCode
        : pending.operation,
      reasonNote: note.trim() || null,
    }, {
      onSuccess: () => {
        setPending(null);
        setNote('');
        setReasonCode('');
        onOperationSuccess?.();
      },
    });
  };

  if (actions.length === 0) return null;

  return (
    <>
      <div className={dense ? 'flex flex-wrap items-center gap-1' : 'flex flex-wrap items-center gap-2'}>
        {actions.map((item) => {
          const Icon = item.blockedReason ? Lock : iconByAction[item.id];
          const label = t(`hire.lateStage.actions.${item.id}`);
          const explanation = item.blockedReason
            ? t(`hire.lateStage.reasons.${item.blockedReason}`)
            : t(`hire.lateStage.tooltips.${item.id}`);
          return (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    type="button"
                    variant="outline"
                    size={dense ? 'icon' : 'sm'}
                    className={dense
                      ? 'h-8 min-h-8 w-8 rounded-lg'
                      : 'h-auto min-h-9 rounded-xl px-3 text-center whitespace-normal'}
                    disabled={!!item.blockedReason || operation.isPending}
                    aria-label={label}
                    title={explanation}
                    onClick={() => requestAction(item.id)}
                  >
                    <Icon className={[
                      dense ? 'h-4 w-4' : 'me-1.5 h-4 w-4 shrink-0',
                      item.id !== 'schedule_f2f' && item.id !== 'disqualify' ? 'rtl-flip' : '',
                    ].join(' ')} />
                    {!dense && label}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-72 text-xs">
                {explanation}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      <AlertDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open && !operation.isPending) {
            setPending(null);
            setNote('');
            setReasonCode('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending ? t(`hire.lateStage.confirmations.${pending.operation}.title`) : ''}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 whitespace-pre-line">
              {pending ? t(`hire.lateStage.confirmations.${pending.operation}.description`) : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            {pending?.operation === 'end_ai_interview_to_f2f' && (
              <>
                <label className="text-sm font-medium">
                  {t('hire.lateStage.endReasonLabel')}
                </label>
                <Select
                  value={reasonCode}
                  disabled={operation.isPending}
                  onValueChange={setReasonCode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('hire.lateStage.endReasonPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      'technical_issue',
                      'candidate_unable_to_continue',
                      'candidate_withdrew_ai_interview',
                      'recruiter_decision',
                      'other',
                    ].map((reason) => (
                      <SelectItem key={reason} value={reason}>
                        {t(`hire.lateStage.endReasons.${reason}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
            <label htmlFor={`late-stage-note-${applicationId}`} className="text-sm font-medium">
              {pending?.operation === 'end_ai_interview_to_f2f' && reasonCode === 'other'
                ? t('hire.lateStage.noteLabelRequired')
                : t('hire.lateStage.noteLabel')}
            </label>
            <Textarea
              id={`late-stage-note-${applicationId}`}
              value={note}
              maxLength={1000}
              disabled={operation.isPending}
              placeholder={t('hire.lateStage.notePlaceholder')}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={operation.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <Button
              type="button"
              disabled={operation.isPending || (
                pending?.operation === 'end_ai_interview_to_f2f' &&
                (!reasonCode || (reasonCode === 'other' && note.trim().length < 3))
              )}
              onClick={confirm}
            >
              {operation.isPending
                ? t('common.saving')
                : t('hire.lateStage.confirmAction')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
